import { useMemo } from 'react';
import type { AdminPayoutRow, AdminPartnerTableRow } from '../../lib/partnerStatsFilters';
import { isPayoutEligible, MIN_PAYOUT_USD } from '../../lib/partnerStatsFilters';
import styles from './MarkPaidModal.module.css';

interface Props {
  partner: AdminPartnerTableRow;
  payouts: AdminPayoutRow[];
  loading: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

export function MarkPaidModal({
  partner,
  payouts,
  loading,
  error,
  onConfirm,
  onClose,
}: Props) {
  const pendingPayouts = useMemo(
    () =>
      payouts
        .filter((p) => p.partnerId === partner.id && p.status !== 'paid')
        .sort((a, b) => b.month.localeCompare(a.month)),
    [payouts, partner.id],
  );

  const totalDue = pendingPayouts.reduce(
    (sum, p) => sum + (p.totalEarnings ?? p.totalCommission ?? 0),
    0,
  );
  const eligible = isPayoutEligible(partner.unpaid);

  return (
    <div className={styles.overlay} onClick={onClose} role="presentation">
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="mark-paid-title"
      >
        <h2 id="mark-paid-title" className={styles.title}>
          Mark as paid
        </h2>
        <p className={styles.subtitle}>
          {partner.displayName} ({partner.referralCode})
        </p>

        <div className={styles.summary}>
          <span>Unpaid balance</span>
          <strong>${partner.unpaid.toFixed(2)}</strong>
        </div>

        {!eligible ? (
          <p className={styles.warning}>
            Below ${MIN_PAYOUT_USD} minimum — not eligible for payout. Partner must reach $
            {MIN_PAYOUT_USD} unpaid balance first.
          </p>
        ) : null}

        {pendingPayouts.length === 0 ? (
          <p className={styles.empty}>No pending monthly payouts for this partner.</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Month</th>
                <th>Installs</th>
                <th>Earnings</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {pendingPayouts.map((p) => (
                <tr key={p.month}>
                  <td>{p.month}</td>
                  <td>{p.totalInstalls ?? '-'}</td>
                  <td>${(p.totalEarnings ?? p.totalCommission ?? 0).toFixed(2)}</td>
                  <td>{p.status ?? 'pending'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {pendingPayouts.length > 0 ? (
          <p className={styles.note}>
            This will mark <strong>{pendingPayouts.length}</strong> pending month
            {pendingPayouts.length === 1 ? '' : 's'} as paid (${totalDue.toFixed(2)} total).
            The partner will see &quot;Paid&quot; on their Earnings tab.
          </p>
        ) : null}

        {error ? <p className={styles.error}>{error}</p> : null}

        <div className={styles.actions}>
          <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.confirmBtn}
            onClick={onConfirm}
            disabled={loading || pendingPayouts.length === 0 || !eligible}
          >
            {loading ? 'Processing…' : 'Mark all pending as paid'}
          </button>
        </div>
      </div>
    </div>
  );
}
